/**
 * EBITDAMarginTrendChartWithToggle — margin trend chart with Chart / Table view toggle.
 *
 * Self-contained single-Card component. Toggle buttons are inline in the header
 * alongside the title (no absolute positioning).
 *
 * Chart view: Gross Margin, EBITDA Margin, EBIT Margin lines over years
 * Table view: Same metrics as columns, most-recent year first
 */
import { useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { BarChart2, TableProperties } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { FocalBadge } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import { formatFiscalYear, formatPercent } from '../utils/formatters';
import {
  FOCAL_COLOR,
  PEER_COLORS,
  gridProps,
  xAxisProps,
  yAxisProps,
  legendProps,
  refLineProps,
  CHART_INK,
  CHART_TYPE,
} from './chartTheme';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
  companyName?: string;
  competitorData?: Record<string, UnifiedFinancialRecord[]>;
}

export default function EBITDAMarginTrendChartWithToggle({
  data,
  region,
  companyName,
  competitorData,
}: Props) {
  const isPeerMode =
    !!competitorData && Object.keys(competitorData).length > 0 && !!companyName;

  // All chart/table cards default to table; user can opt into chart view.
  const [view, setView] = useState<'chart' | 'table'>('table');

  // Multi-company EBITDA margin trend overlay
  if (isPeerMode) {
    // Build a year-keyed merge: { year: 2020, focal: 18.5, peerA: 22.1, ... }
    const allYears = new Set<number>();
    data.forEach((d) => allYears.add(d.year));
    Object.values(competitorData!).forEach((records) =>
      records.forEach((r) => allYears.add(r.year)),
    );

    const focalSeries = new Map<number, number | null>();
    data.forEach((d) => {
      focalSeries.set(d.year, d.ebitda_margin != null ? d.ebitda_margin * 100 : null);
    });

    const peerSeries: Record<string, Map<number, number | null>> = {};
    Object.entries(competitorData!).forEach(([name, records]) => {
      const m = new Map<number, number | null>();
      records.forEach((r) => {
        m.set(r.year, r.ebitda_margin != null ? r.ebitda_margin * 100 : null);
      });
      peerSeries[name] = m;
    });

    const sortedYears = [...allYears].sort((a, b) => a - b);
    const peerNames = Object.keys(peerSeries);
    const chartData = sortedYears.map((year) => {
      const row: Record<string, any> = { year: formatFiscalYear(year, region) };
      row[companyName!] = focalSeries.get(year) ?? null;
      peerNames.forEach((name) => {
        row[name] = peerSeries[name].get(year) ?? null;
      });
      return row;
    });

    if (chartData.length < 2) return null;

    const tableYearsDesc = [...sortedYears].sort((a, b) => b - a);

    return (
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-neutral-800">
                10-Year EBITDA Margin Trend — Multi-Company Comparison (%)
              </h3>
              <p className="text-xs text-neutral-500 mt-0.5">
                EBITDA margin trajectory for focal company and peers.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <FocalBadge name={companyName!} />
              <div className="flex items-center rounded-lg border border-neutral-200 bg-white p-0.5 shadow-sm">
                <button
                  onClick={() => setView('chart')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    view === 'chart'
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <BarChart2 className="w-3.5 h-3.5" />
                  Chart
                </button>
                <button
                  onClick={() => setView('table')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    view === 'table'
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <TableProperties className="w-3.5 h-3.5" />
                  Table
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        {view === 'chart' ? (
          <CardBody>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 16, left: 8, bottom: 8 }}
                barCategoryGap="18%"
                barGap={2}
              >
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="year" {...xAxisProps} />
                <YAxis
                  {...yAxisProps}
                  tickFormatter={(v) => `${v?.toFixed(0)}%`}
                />
                <Tooltip
                  content={<ChartTooltip format={(v) => `${v.toFixed(1)}%`} />}
                  cursor={{ fill: CHART_INK.grid }}
                />
                <Legend {...legendProps} />
                {/* Focal first so it appears leftmost in each year group */}
                <Bar
                  dataKey={companyName!}
                  fill={FOCAL_COLOR}
                  radius={[3, 3, 0, 0]}
                />
                {peerNames.map((name, idx) => (
                  <Bar
                    key={name}
                    dataKey={name}
                    fill={PEER_COLORS[idx % PEER_COLORS.length]}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        ) : (
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                    <th className="text-left py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider sticky left-0 bg-neutral-50 z-10">
                      Year
                    </th>
                    <th className="text-right py-3 px-4 text-secondary-800 font-bold uppercase tracking-wider whitespace-nowrap">
                      ★ {companyName}
                    </th>
                    {peerNames.map((name) => (
                      <th
                        key={name}
                        className="text-right py-3 px-4 text-neutral-600 font-semibold uppercase tracking-wider whitespace-nowrap"
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableYearsDesc.map((year, rowIdx) => {
                    const focalVal = focalSeries.get(year);
                    return (
                      <tr
                        key={year}
                        className={`border-b border-neutral-100 hover:bg-blue-50/30 ${
                          rowIdx === 0 ? 'bg-blue-50/20' : ''
                        }`}
                      >
                        <td className="py-3 px-4 font-semibold text-neutral-800 tabular-nums sticky left-0 bg-white">
                          {formatFiscalYear(year, region)}
                          {rowIdx === 0 && (
                            <span className="ml-1.5 text-[9px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full font-bold">
                              Latest
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right tabular-nums font-semibold text-secondary-800">
                          {focalVal != null ? `${focalVal.toFixed(1)}%` : '—'}
                        </td>
                        {peerNames.map((name) => {
                          const v = peerSeries[name].get(year);
                          return (
                            <td
                              key={name}
                              className="py-3 px-4 text-right tabular-nums text-neutral-700"
                            >
                              {v != null ? `${v.toFixed(1)}%` : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardBody>
        )}
      </Card>
    );
  }

  // ── Shared data preparation ────────────────────────────────────────────────
  const chartData = data.map((d) => {
    const revenue = d.total_revenue ?? d.revenue_from_operations;
    const grossProfit =
      d.gross_profit ??
      (revenue != null && d.direct_cost != null ? revenue - d.direct_cost : null) ??
      (d.ebitda != null && d.employee_expenses != null && d.other_expenses != null
        ? d.ebitda + d.employee_expenses + d.other_expenses
        : null);
    return {
      year: formatFiscalYear(d.year, region),
      ebitdaMargin: d.ebitda_margin != null ? d.ebitda_margin * 100 : null,
      ebitMargin: d.ebit != null && revenue ? (d.ebit / revenue) * 100 : null,
      grossMargin: grossProfit != null && revenue ? (grossProfit / revenue) * 100 : null,
    };
  });

  const avgEbitda =
    (data.reduce((sum, d) => sum + (d.ebitda_margin ?? 0), 0) / data.length) * 100;

  const tableRows = [...data].reverse();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-sm font-semibold text-neutral-800">EBITDA Margin Trend (%)</h3>
          {/* Inline toggle — no absolute positioning */}
          <div className="flex items-center rounded-lg border border-neutral-200 bg-white p-0.5 shadow-sm shrink-0">
            <button
              onClick={() => setView('chart')}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                view === 'chart'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              <BarChart2 className="w-3.5 h-3.5" />
              Chart
            </button>
            <button
              onClick={() => setView('table')}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                view === 'table'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              <TableProperties className="w-3.5 h-3.5" />
              Table
            </button>
          </div>
        </div>
      </CardHeader>

      {view === 'chart' ? (
        <CardBody>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 20, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="year" {...xAxisProps} />
              <YAxis
                {...yAxisProps}
                tickFormatter={(v) => `${v?.toFixed(0)}%`}
              />
              <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(1)}%`} />} />
              <Legend {...legendProps} />
              <ReferenceLine
                y={avgEbitda}
                {...refLineProps}
                label={{ value: 'Avg', fill: CHART_INK.muted, fontSize: CHART_TYPE.axisTick }}
              />
              {/* Three margins on one chart — a medium→darkest purple ramp
                  (plus a dash on EBIT) keeps the lines distinguishable. */}
              <Line
                type="monotone"
                dataKey="grossMargin"
                name="Gross Margin"
                stroke={PEER_COLORS[3]}
                dot={{ r: 3 }}
                strokeWidth={2.5}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="ebitdaMargin"
                name="EBITDA Margin"
                stroke={PEER_COLORS[0]}
                dot={{ r: 3 }}
                strokeWidth={2}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="ebitMargin"
                name="EBIT Margin"
                stroke={FOCAL_COLOR}
                dot={{ r: 3 }}
                strokeWidth={1.5}
                strokeDasharray="4 2"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </CardBody>
      ) : (
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                  <th className="text-left py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider w-24">
                    Year
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    Gross Margin
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    EBITDA Margin
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    EBIT Margin
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((d, i) => {
                  const revenue = d.total_revenue ?? d.revenue_from_operations;
                  const grossProfit =
                    d.gross_profit ??
                    (revenue != null && d.direct_cost != null
                      ? revenue - d.direct_cost
                      : null) ??
                    (d.ebitda != null &&
                    d.employee_expenses != null &&
                    d.other_expenses != null
                      ? d.ebitda + d.employee_expenses + d.other_expenses
                      : null);
                  const grossMargin =
                    grossProfit != null && revenue ? grossProfit / revenue : null;
                  const ebitMargin =
                    d.ebit != null && revenue ? d.ebit / revenue : null;

                  return (
                    <tr
                      key={d.year}
                      className={`border-b border-neutral-100 hover:bg-blue-50/30 transition-colors ${
                        i === 0 ? 'bg-blue-50/20' : ''
                      }`}
                    >
                      <td className="py-3 px-4 font-semibold text-neutral-800 tabular-nums">
                        {formatFiscalYear(d.year, region)}
                        {i === 0 && (
                          <span className="ml-1.5 text-[9px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full font-bold">
                            Latest
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-secondary-600 font-medium">
                        {grossMargin != null ? formatPercent(grossMargin) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-secondary-800 font-semibold">
                        {d.ebitda_margin != null ? formatPercent(d.ebitda_margin) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-neutral-700">
                        {ebitMargin != null ? formatPercent(ebitMargin) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      )}
    </Card>
  );
}
